import React, { FC, useContext, useEffect, useState } from 'react';
import styles from './PermissionsForm.module.scss';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { ArrowLeft, Copy, Button, Select, Accordion, Checkbox, Modal } from 'esa-ui-library';
import { ROLE_UPDATE_MESSAGE, COPY_PERMISSIONS_TITLE, COPY_PERMISSIONS_MESSAGE } from '../../../..';
import { useStore, PropertyType, CUSTOM_ROLE, DEFAULT_PERMISSIONS } from '../../../../../common';
import { AuthContext } from '../../../../../logic';

interface PermissionsProps {
	onBack: () => void;
	onSubmit: () => void;
}

const PermissionsForm: FC<PermissionsProps> = ({ onBack, onSubmit }) => {
	const [modalOpened, setModalOpened] = useState(false);
	const [state, dispatch] = useStore();
	const { roles, activeRoles, customRole, permissionGroups, activePermissions, openedGroups, users, activeUsers } =
		state;
	const { token } = useContext(AuthContext);

	useEffect(() => {
		dispatch('ROLES_INIT', { token });
		dispatch('USERS_INIT', { token });
		dispatch('PERMISSIONS_INIT', token);
	}, []);

	const onRolesChange = (roles: PropertyType[]) => {
		dispatch('ROLES_UPDATE', roles);
	};

	const onPermissionsChange = (val: string) => {
		dispatch('PERMISSIONS_UPDATE', val);
	};

	const onAllPermissionsChange = (permissions: string[]) => {
		dispatch('ALL_PERMISSIONS_UPDATE', permissions);
	};

	const onModalOpen = () => {
		setModalOpened(true);
	};

	const onModalClose = () => {
		setModalOpened(false);
		onPermissionsUserSelect([]);
	};

	const onPermissionsUserSelect = (users: PropertyType[]) => {
		dispatch('USERS_UPDATE', users);
	};

	const onPermissionsCopy = () => {
		dispatch('PERMISSIONS_COPY', token);
		onModalClose();
	};

	const onGroupOpen = (group: string) => {
		dispatch('GROUP_OPEN', group);
	};

	const onGroupClose = (group: string) => {
		dispatch('GROUP_CLOSE', group);
	};

	const onGroupChange = (group: string, open: boolean) => {
		open ? onGroupOpen(group) : onGroupClose(group);
	};

	const renderBulkCheckbox = (permissions: PropertyType[]) => {
		const active = permissions.filter((p) => activePermissions.includes(p.name));
		const allActive = !!active.length && active.length === permissions.length;
		const update = allActive ? [...DEFAULT_PERMISSIONS] : permissions.map((p) => p.name);

		return (
			<Checkbox
				checked={allActive}
				partial={!!active.length}
				onChange={() => onAllPermissionsChange(update)}
				dataId='all-permissions-check'
			/>
		);
	};

	const renderPermissions = (permissions: PropertyType[]) =>
		permissions.map((p) => (
			<li key={uuid()} className={styles.Permission}>
				{p.description}
				<Checkbox
					checked={activePermissions.includes(p.name)}
					disabled={DEFAULT_PERMISSIONS.includes(p.name)}
					onChange={() => onPermissionsChange(p.name)}
					dataId='permission-check'
				/>
			</li>
		));

	const renderPermissionGroups = () =>
		permissionGroups.map((pg: PropertyType) => {
			return (
				<Accordion
					key={uuid()}
					title={pg.name}
					opened={openedGroups.includes(pg.name)}
					headerContent={renderBulkCheckbox(pg.permissions)}
					onChange={(open) => onGroupChange(pg.name, open)}
				>
					<ul className={styles.PermissionGroup}>{renderPermissions(pg.permissions)}</ul>
				</Accordion>
			);
		});

	return (
		<>
			<section className={styles.Permissions}>
				<div onClick={onBack} className={styles.Back} data-testid='back-to-profile'>
					<ArrowLeft className={styles.Arrow} /> Back to profile
				</div>
				<section className={styles.RoleUpdate}>
					<div>
						<span className={styles.UserRole}>User role</span>
						<p className={styles.RoleMessage}>{ROLE_UPDATE_MESSAGE}</p>
					</div>
					<Select
						selected={customRole ? [CUSTOM_ROLE] : activeRoles}
						options={roles}
						multiple
						removeable={!customRole}
						placeholder='Select roles'
						noMargin
						className={styles.RoleSelect}
						onChange={onRolesChange}
						dataId='role-field'
					/>
				</section>
				<section>
					<section className={styles.PermissionsHeader}>
						<h4>Permissions</h4>
						<Button teritary onClick={onModalOpen} dataId='modal-button'>
							<Copy className={styles.Copy} /> {COPY_PERMISSIONS_TITLE}
						</Button>
					</section>
					{renderPermissionGroups()}
				</section>
				<Button primary className={styles.Submit} onClick={onSubmit} dataId='create-user-button'>
					Create User
				</Button>
			</section>
			{modalOpened && (
				<Modal
					title={COPY_PERMISSIONS_TITLE}
					submitLabel='Save'
					primary
					onCancel={onModalClose}
					onSubmit={onPermissionsCopy}
				>
					<div className={styles.CopyMessage}>{COPY_PERMISSIONS_MESSAGE}</div>
					<Select
						selected={activeUsers}
						options={users}
						label='User to copy permissions from'
						placeholder='Choose user'
						noMargin
						search
						className={styles.UserSelect}
						onChange={onPermissionsUserSelect}
						dataId='users-field'
					/>
				</Modal>
			)}
		</>
	);
};

PermissionsForm.propTypes = {
	onBack: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
};

export { PermissionsForm };
